import { createApp } from "vue";
import App from "./App.vue";
import router from "./router/index.js";
import store from "./store";
import i18n from "./i18n";
import NotificationPlugin from "./components/NotificationPlugin";
import { BootstrapVue3 } from "bootstrap-vue-3";
// Bootstrap 전역 CSS 제외 (테스트) — Tailwind 와 유틸 클래스명 충돌(.border/.text-muted 등)로
// 하얀 테두리·색 강제 등이 발생해 잠시 비활성화. 되돌리려면 주석 해제.
// ⚠️ bootstrap-vue-3 컴포넌트(b-table 등)와 Bootstrap 클래스 기반 스타일이 영향받음.
// import "bootstrap/dist/css/bootstrap.min.css";
import VueCookies from "vue-cookies";
import { quillEditor } from "vue3-quill";
import axios from "axios";
import { BASE_PREFIX } from "./config";
import { feat } from "./config/tenantFeatures";
import { saveRedirect } from "./utils/authRedirect";
import { ensureChatConnected, chatDisConnect } from "./services/socket";
import "./assets/tailwind.css";
import "./assets/scss/mdt-dashboard.scss";

// ─── 클라이언트 컨텍스트 헤더 (모든 API 요청에 자동 주입) ───────────────
// 서버(Auth)가 이 헤더를 읽어 시스템 로그의 Platform/ClientApp/AppVersion 을 채운다.
const CLIENT_PLATFORM = "web";
const CLIENT_APP = "mdt-platform";
const APP_VERSION = "1.0.0";
axios.interceptors.request.use((config) => {
  config.headers["X-Client-Platform"] = CLIENT_PLATFORM;
  config.headers["X-Client-App"] = CLIENT_APP;
  config.headers["X-App-Version"] = APP_VERSION;
  // 서브경로(/rmt) 배포: 루트기준 /api·/uploads 호출에 프리픽스 자동 부여.
  // (절대 URL, 이미 프리픽스된 것, AUTH_URL(/rmt/api/auth) 은 제외)
  if (BASE_PREFIX && typeof config.url === "string" &&
      /^\/(api|uploads|Uploads)(\/|$|\?)/.test(config.url) &&
      !config.url.startsWith(BASE_PREFIX + "/")) {
    config.url = BASE_PREFIX + config.url;
  }
  return config;
});

// ─── 세션 무효화(다른 기기 로그인 등으로 REVOKE) 감지 → 자체 로그아웃 ─────────
// Auth 가드가 세션이 LOGIN 이 아니면 401 { message: "SESSION_INVALID" } 을 준다.
// (일반 401: 비밀번호 오류 등은 message 가 달라 무시 → 오작동 방지)
let sessionInvalidHandled = false;
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const msg = error?.response?.data?.message;
    const url = error?.config?.url || "";
    // session/check 는 라우터 beforeEach 가 직접 처리(만료 포함) → 여기선 제외해 이중 리다이렉트 방지.
    // 로그인/공개 엔드포인트(비번오류 등)의 401 은 제외. 그 외 "로그인 상태에서의 401"
    //  = 세션 REVOKE(SESSION_INVALID) 또는 access token 만료 → 세션 만료로 보고 로그인 화면으로.
    //  (기존엔 SESSION_INVALID 만 처리해 토큰만료 401 이 에러화면으로 노출됐음)
    const isPreAuth = /\/auth\/(login|token|pin-login|refresh|register|public-key|check-id|check-email|forgotPassword|validateResetToken|resetPassword|exchange-token)/.test(url);
    // 본인확인용 크리덴셜 변경(비밀번호/PIN)·탈퇴의 401 = "현재 비밀번호 오류" 같은 폼 검증 실패지
    // 세션 만료가 아니다 → 자동 로그아웃/리다이렉트에서 제외(각 화면이 에러 메시지로 처리).
    // (pin-login 은 위 isPreAuth 에서 이미 처리 — 여기 정규식은 /auth/pin, /auth/password 만 매칭)
    const isSelfCredential = /\/auth\/(password|pin|withdraw)(?:$|[/?])/.test(url);
    const hadSession = !!localStorage.getItem("accessToken");
    if (
      status === 401 &&
      !sessionInvalidHandled &&
      !url.includes("/session/check") &&
      !isPreAuth &&
      !isSelfCredential &&
      (msg === "SESSION_INVALID" || hadSession)
    ) {
      sessionInvalidHandled = true;
      try {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        delete axios.defaults.headers.common.Authorization;
        chatDisConnect(); // 세션 종료 → 좀비 소켓 정리(모니터 미매핑 집계 제거)
        store.commit("logoutSuccess"); // loginStore 만 리셋(theme 보존). persistedstate 가 동기 저장 후 리다이렉트
      } catch (e) {
        /* 정리 실패 무시 */
      }
      // 복귀 경로 저장(URL 에 returnUrl 노출 없이) — 로그인 성공 후 이 경로로 돌아감.
      // 하드 리다이렉트라 메모리/router state 는 날아가므로 sessionStorage 사용.
      saveRedirect(window.location.pathname + window.location.search + window.location.hash);
      // 하드 리다이렉트 — 라우터 가드가 토큰 없음을 보고 로그인으로 보냄
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// access_token(JWT)이 만료됐는지 로컬 판정. exp(초) < now 면 만료.
// 디코드 실패(형식 이상·exp 없음)는 판단 불가 → false 반환해 기존 동작(연결) 폴백.
function isAccessTokenExpired(token) {
  try {
    const payload = JSON.parse(
      decodeURIComponent(
        escape(
          atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
        ),
      ),
    );
    if (!payload || !payload.exp) return false; // exp 없으면 판단 보류(연결)
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    return false; // 디코드 실패 → 연결 폴백(과잉 차단 방지)
  }
}

// 부팅 시(F5/새 탭) 이미 로그인 상태면 토큰/소켓 복원
try {
  const token = localStorage.getItem("accessToken");
  // Authorization 헤더 복원 — 없으면 JWT 가드 API(verify-password, pin, withdraw 등)가 401
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
  const uid = JSON.parse(localStorage.getItem("vuex") || "{}")?.loginStore
    ?.userInfo?.UserId;
  // 만료 토큰이면 소켓을 붙이지 않는다 — 붙이면 서버가 session_id 추출 실패로
  // 모니터에 "세션 미매핑" 좀비로 집계됨. (session/check 401 → 로그인 리다이렉트가 처리)
  if (token && uid && !isAccessTokenExpired(token)) ensureChatConnected(uid);
} catch (e) {
  /* 로컬스토리지 파싱 실패 시 무시 (로그인 시 재연결됨) */
}

const app = createApp(App)
  .use(VueCookies, {
    expireTimes: "1d",
    secure: true,
  })
  .use(BootstrapVue3)
  .use(router)
  .use(store)
  .use(i18n)
  .use(NotificationPlugin)
  .use(quillEditor);
// 테넌트별 UI 기능 토글 전역 등록 → 템플릿에서 $feat('key') 로 사용
app.config.globalProperties.$feat = feat;
app.mount("#app");
